// 投资运营企业（运营侧）佐证材料覆盖表
//
// 【背景】源文档《企业级数据贯通评价方案-三方十类-v2.0.2.docx》存在缺陷：
//   附录 15/16（投资运营·运营侧）与附录 1/2（投资运营·建设侧）的佐证材料逐行完全相同，
//   经脚本比对确认 17/17 行（在线版）、41/41 行（全量版）一致，企业级与项目级均如此。
//
// 【处理】依据文档 2.4 节「投资运营企业（运营侧）」四项核心需求重写本角色佐证：
//   ① 建设数据资产接收 ② 运维业务贯通 ③ 监测数据融合应用 ④ 数据驱动决策
//   企业级取「制度/架构/清单类」材料，项目级取「过程/记录/报告类」材料（对应文档 2.3 节）。
//   项目级同时参照文档表 19《投资运营（运营侧）项目级典型佐证材料清单》。
//
// 【状态】本表为 AI 草拟，需人工复核后方可定稿。复核通过后删除本段说明即可。
// 【结构】覆盖 evidence.js 中 roleId = 'invest-operation' 的条目，其余角色不受影响。

/** 各能力域的通用佐证材料池（企业级 = 制度/架构/清单类；项目级 = 过程/记录/报告类） */
const POOLS = {
  enterprise: {
    strategy: '运营数据战略与资产数字化规划、运营数据治理委员会章程、运营数据资产入表制度、资产数字化KPI台账',
    security: '运营数据分级分类管理办法、运营数据安全责任书、合规审计计划、等保测评/认证证书',
    arch: '资产主数据模型、运营数据分布与容灾规划、运维数据中台架构、监测系统集成接入清单、平台备份与恢复验证记录、数据传输加密配置、运营数据转换规则库',
    quality: '运营数据更新时效SLA、资产核心数据准确率报表、运营数据质量分析报告、运营数据标准文档、资产统一数据门户、数据防篡改存证、业财一致性核对记录、数据清洗规则',
    value: '资产绩效度量框架、运营分析看板、资产可视化大屏、运营养管创新案例、数据资产目录与入表凭证、AI预测性维护模型、生态数据共享协议',
    bim: '竣工BIM模型接收与资产化记录、模型-资产映射完整率、自动化交付（运维工单）日志'
  },
  project: {
    strategy: '竣工数据资产接收确认单、项目运营数据管理细则、运营数据资产移交清单、运营数据质量KPI台账',
    security: '运维数据分级分类实施细则、数据访问授权审批表、运营合规检查记录、系统安全配置核查记录',
    arch: '资产主数据模型（项目）、运维数据分布与备份方案、运维管理系统部署拓扑图、监测系统集成接口清单、平台备份与恢复验证记录、数据传输加密配置记录、运营数据转换规则库（项目）',
    quality: '运营数据更新时效SLA、资产核心数据准确率核查报表、运营月度数据质量报告、运营数据标准执行记录、资产统一数据门户、数据防篡改校验记录、业财数据一致核对记录、数据清洗规则（项目）',
    value: '资产绩效度量框架（项目）、运营数据分析看板、资产可视化大屏、运营养管创新案例、项目数据资产目录、AI预测性养护模型、跨参与方数据共享协议',
    bim: '竣工BIM模型接收与资产化记录（项目）、模型-资产映射完整率、自动化交付（运维工单）日志（项目）'
  }
}

/** 各指标的本条专属凭证（proof）。结构：PROOFS[级别][版本][指标 id] */
const PROOFS = {
  // ---------------------------------------------------------------- 企业级
  enterprise: {
    // 快速版 17 项
    online: {
      'strategy-1': '运营数据战略发布文件与资产数字化路线图（含KPI挂钩清单）',
      'strategy-2': '运营数据培训覆盖率统计（按部门/岗位）',
      'security-1': '资产与监测数据分级定级清单（定级覆盖率）',
      'security-2': '合规义务满足率清单（含等保测评证书）',
      'security-3': '安全审计覆盖与整改闭环率报表',
      'arch-1': '资产主数据模型与开发规范覆盖率清单',
      'arch-2': '运维平台系统接入率与数据入湖率清单',
      'arch-3': '平台可用率、监控覆盖率与告警闭环率报表',
      'arch-4': '资产-工单-养护接口目录与问题闭环率报表',
      'quality-1': '资产数据标准覆盖率与核心字段准确率报表',
      'quality-2': '运营数据按时更新率与门户可访问率报表',
      'quality-3': '质量指标覆盖率与缺陷清洗率统计',
      'value-1': '资产分析覆盖率与运营看板清单',
      'value-2': '资产确权入表率与AI预测性养护场景覆盖率台账',
      'value-3': '运营创新应用率与生态合作覆盖率清单',
      'bim-1': 'IFC语义一致率与模型-资产映射完整率报表',
      'bim-2': '运维工单自动化交付率统计'
    },
    // 完整版 41 项
    full: {
      'strategy-1': '运营数据战略发布文件与资产数字化路线图（含决策层审批）',
      'strategy-2': '运营数据治理职责矩阵（委员会/专岗/权责）',
      'strategy-3': '运营数据培训覆盖率统计（按部门）',
      'strategy-4': '运营数据人才达标率台账（持证/能力模型）',

      'security-1': '运营数据安全总方针与分级分类原则文件',
      'security-2': '等保测评/认证证书与定级备案清单',
      'security-3': '安全管理岗职责与事件响应演练记录',
      'security-4': '运营合规义务清单与合规满足率台账',
      'security-5': '运营数据安全风险评估报告与高风险处置台账',
      'security-6': '安全审计计划、审计报告与整改闭环率报表',

      'arch-1': '运维平台监控覆盖率与告警闭环率报表',
      'arch-2': '资产-工单-养护跨部门数据流转规程与接口目录',
      'arch-3': '运营数据问题反馈与闭环处理记录',
      'arch-4': '资产主数据模型覆盖率清单',
      'arch-5': '运营数据分布与容灾规划文件',
      'arch-6': '运营数据开发规范与版本评审记录',
      'arch-7': '运维数据中台架构与元数据目录清单',
      'arch-8': '运营数据仓库建设与主题域覆盖清单',
      'arch-9': '运营数据湖入湖率与服务复用率清单',
      'arch-10': '监测系统集成接入清单与调度自动化率统计',
      'arch-11': '平台可用率、备份恢复演练与故障处理记录',
      'arch-12': '监测数据传输加密配置与传输成功率审计记录',
      'arch-13': '运营数据转换规则库与转换正确率统计',

      'quality-1': '运营数据按时更新率统计（按SLA）',
      'quality-2': '资产核心字段准确率核查报表',
      'quality-3': '运营数据质量分析报告（含根因定位）',
      'quality-4': '运营数据标准文档与标准覆盖率清单',
      'quality-5': '资产统一数据门户可访问率与API响应统计',
      'quality-6': '资产数据完整性校验与防篡改存证记录',
      'quality-7': '业财/资产-工单系统间一致性核对记录',
      'quality-8': '数据清洗规则库与清洗覆盖率统计',

      'value-1': '资产绩效度量框架与KPI台账',
      'value-2': '运营分析看板清单与业务分析覆盖率统计',
      'value-3': '资产可视化大屏清单与可视化覆盖率统计',
      'value-4': '运营养管创新案例与创新应用率台账',
      'value-5': '数据资产目录、确权与入表凭证清单',
      'value-6': 'AI预测性维护模型清单与智能化场景覆盖率台账',
      'value-7': '生态数据共享协议与合作覆盖率清单',

      'bim-1': 'IFC语义一致率检测报告（竣工模型）',
      'bim-2': '建设-运营模型传递完整率与版本追溯报表',
      'bim-3': '运维工单流程引擎自动化交付率统计'
    }
  },
  // ---------------------------------------------------------------- 项目级
  project: {
    // 快速版 17 项
    online: {
      'strategy-1': '《竣工数据资产接收确认单》与运营数据管理小组成立文件',
      'strategy-2': '运营数据培训签到与覆盖统计（按岗位）',
      'security-1': '运维数据分级定级清单（项目）',
      'security-2': '运营合规检查记录与系统安全配置核查记录',
      'security-3': '安全自查记录与整改闭环台账',
      'arch-1': '资产主数据模型与开发规范自查清单（项目）',
      'arch-2': '监测系统与运维平台接入清单及接口调用日志',
      'arch-3': '平台运行状态监控截图与告警-工单处理日志',
      'arch-4': '资产-工单-养护数据关联查询截图与问题反馈闭环记录',
      'quality-1': '资产核心数据准确率核查报表（项目）',
      'quality-2': '运营数据按时更新率统计与门户查询截图',
      'quality-3': '运营月度数据质量报告与清洗记录',
      'value-1': '资产运营数据分析看板截图',
      'value-2': 'AI预测性养护模型应用场景清单与资产台账截图',
      'value-3': '运营创新应用案例与跨参与方数据共享记录',
      'bim-1': 'BIM模型导入运维平台验证记录与IFC语义一致率检测报告',
      'bim-2': '自动化交付（运维工单）日志（项目）'
    },
    // 完整版 41 项
    full: {
      'strategy-1': '《竣工数据资产接收确认单》与运营数据管理细则',
      'strategy-2': '运营数据管理小组与数据负责人任命文件',
      'strategy-3': '运营数据培训签到与覆盖统计（按岗位）',
      'strategy-4': '运营数据岗位配置与持证情况台账',

      'security-1': '运维数据安全实施细则（项目）',
      'security-2': '系统安全配置核查记录与等保备案（项目）',
      'security-3': '数据访问授权审批表与安全事件响应演练记录',
      'security-4': '运营合规检查记录（项目）',
      'security-5': '运营数据安全风险评估与处置台账（项目）',
      'security-6': '安全自查记录与整改闭环台账（项目）',

      'arch-1': '平台运行状态监控截图与告警-工单处理日志',
      'arch-2': '资产-工单-养护数据流转规程与接口目录（项目）',
      'arch-3': '运营数据问题反馈闭环记录（项目）',
      'arch-4': '资产主数据模型自查清单（项目）',
      'arch-5': '运维数据分布与备份方案（项目）',
      'arch-6': '运营数据开发规范与版本评审记录（项目）',
      'arch-7': '运维数据中台/平台部署拓扑图（项目）',
      'arch-8': '运营数据仓库/主题域建设清单（项目）',
      'arch-9': '运营数据入湖率与服务复用率清单（项目）',
      'arch-10': '监测系统集成接口清单与调用日志',
      'arch-11': '平台备份与恢复验证记录、故障处理日志',
      'arch-12': '监测数据传输加密配置记录与传输成功率统计',
      'arch-13': '运营数据转换规则库与转换正确率统计（项目）',

      'quality-1': '运营数据按时更新率统计（按SLA，项目）',
      'quality-2': '资产核心数据准确率核查报表（项目）',
      'quality-3': '运营月度数据质量报告（含根因）',
      'quality-4': '运营数据标准执行记录（项目）',
      'quality-5': '资产统一数据门户查询截图与响应统计',
      'quality-6': '资产数据完整性校验与防篡改校验记录',
      'quality-7': '业财/资产-工单一致性核对记录（项目）',
      'quality-8': '数据清洗规则与清洗覆盖率统计（项目）',

      'value-1': '资产绩效度量框架与KPI台账（项目）',
      'value-2': '运营数据分析看板截图与分析覆盖率统计',
      'value-3': '资产可视化大屏截图（项目）',
      'value-4': '运营养管创新案例清单（项目）',
      'value-5': '项目数据资产目录与竣工移交凭证',
      'value-6': 'AI预测性养护模型应用场景清单（项目）',
      'value-7': '跨参与方数据共享协议与共享记录',

      'bim-1': 'BIM模型导入运维平台验证记录与IFC语义一致率检测报告',
      'bim-2': '建设-运营模型传递完整率与版本追溯记录',
      'bim-3': '自动化交付（运维工单）日志（项目）'
    }
  }
}

/** 展开为 evidence.js 同构的覆盖表：OVERRIDE[级别][版本][指标 id] = { common, proof } */
function build () {
  const out = {}
  for (const level of Object.keys(PROOFS)) {
    out[level] = {}
    for (const edition of Object.keys(PROOFS[level])) {
      const byIndicator = {}
      for (const [indicatorId, proof] of Object.entries(PROOFS[level][edition])) {
        const domain = indicatorId.split('-')[0]
        byIndicator[indicatorId] = { common: POOLS[level][domain], proof }
      }
      out[level][edition] = byIndicator
    }
  }
  return out
}

export const OPERATION_EVIDENCE_OVERRIDE = build()

/** 需要应用覆盖表的角色 id */
export const OVERRIDE_ROLE_IDS = ['invest-operation']
